import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  ArrowLeft,
  Loader,
  AlertCircle,
  CheckCircle2,
  Package,
  MapPin,
  Check,
  X,
  ChevronDown,
  ChevronRight,
  Scan,
  RefreshCw,
  Send,
  ShieldCheck
} from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { countingListCreationService } from '../../../../services/countingListCreationService';
import { userProfileService } from '../../../../services/userProfileService';
import { systemSetupService } from '../../../../services/systemSetupService';
import { CountingListOverviewItem, CountingListLine } from '../../../../types/countingListCreation';
import { parseGS1Barcode } from '../../../../utils/gs1Parser';
import { useSimpleBarcodeScanner } from '../../../../hooks/useSimpleBarcodeScanner';
import { MobileBarcodeCaptureInput } from '../../../common/scanning';
import { useBarcodeScannerEnabled } from '../../../../hooks/useBarcodeScannerEnabled';

interface CountingListCountingProps {
  list: CountingListOverviewItem;
  onBack: () => void;
  onCompleted?: () => void;
}

interface CountingLine extends CountingListLine {
  isExpanded?: boolean;
  isCounting?: boolean;
}

type ConfirmStep = 'location' | 'item' | 'count';

export const CountingListCounting: React.FC<CountingListCountingProps> = ({ list, onBack, onCompleted }) => {
  const { user } = useAuth();
  const [lines, setLines] = useState<CountingLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeLineId, setActiveLineId] = useState<number | null>(null);
  const [countInput, setCountInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const countInputRef = useRef<HTMLInputElement>(null);
  const locationScanRef = useRef<HTMLInputElement>(null);
  const itemScanRef = useRef<HTMLInputElement>(null);
  const barcodeScannerEnabled = useBarcodeScannerEnabled();

  // MaxLineItemQuantity from system setup
  const [maxLineItemQuantity, setMaxLineItemQuantity] = useState<number | null>(null);
  const [showQuantityWarning, setShowQuantityWarning] = useState(false);
  const [pendingSaveLineId, setPendingSaveLineId] = useState<number | null>(null);
  const [pendingSaveQty, setPendingSaveQty] = useState<number>(0);

  // Barcode confirm settings from user profile
  const [requireLocationScan, setRequireLocationScan] = useState(false);
  const [requireItemScan, setRequireItemScan] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);

  // Confirmation step state
  const [confirmStep, setConfirmStep] = useState<ConfirmStep>('count');
  const [locationScanInput, setLocationScanInput] = useState('');
  const [itemScanInput, setItemScanInput] = useState('');
  const [scanError, setScanError] = useState<string | null>(null);

  // Load user profile settings and system setup
  useEffect(() => {
    userProfileService.getCurrentUserProfile().then(profile => {
      if (profile) {
        setRequireLocationScan(profile.barCodeConfirmLocation ?? false);
        setRequireItemScan(profile.barCodeConfirmItem ?? false);
      }
      setProfileLoaded(true);
    }).catch(() => {
      setProfileLoaded(true);
    });

    systemSetupService.getCurrent().then(setup => {
      if (setup.maxLineItemQuantity != null && setup.maxLineItemQuantity > 0) {
        setMaxLineItemQuantity(setup.maxLineItemQuantity);
      }
    }).catch(err => {
      console.warn('Kunne ikke hente MaxLineItemQuantity fra systeminnstillinger:', err);
    });
  }, []);

  // Determine the first confirm step based on settings
  // Order: Item → Location → Count
  const getFirstConfirmStep = useCallback((): ConfirmStep => {
    if (requireItemScan) return 'item';
    if (requireLocationScan) return 'location';
    return 'count';
  }, [requireLocationScan, requireItemScan]);

  // Determine next step after current
  // Order: Item → Location → Count
  const getNextStep = useCallback((current: ConfirmStep): ConfirmStep => {
    if (current === 'item') return requireLocationScan ? 'location' : 'count';
    if (current === 'location') return 'count';
    return 'count';
  }, [requireLocationScan]);

  const loadLines = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await countingListCreationService.getCountingListLines(list.batchID);
      setLines(result.map(l => ({ ...l, isExpanded: false, isCounting: false })));
    } catch (err) {
      console.error('Error loading counting list lines:', err);
      setError('Kunne ikke laste tellelistelinjer');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLines();
  }, [list.batchID]);

  // Focus the appropriate input based on confirm step
  useEffect(() => {
    if (activeLineId === null) return;
    if (confirmStep === 'count' && countInputRef.current) {
      countInputRef.current.focus();
      countInputRef.current.select();
    } else if (confirmStep === 'location' && locationScanRef.current) {
      locationScanRef.current.focus();
    } else if (confirmStep === 'item' && itemScanRef.current) {
      itemScanRef.current.focus();
    }
  }, [activeLineId, confirmStep]);

  // Find the active line object
  const activeLine = useMemo(() => {
    if (activeLineId === null) return null;
    return lines.find(l => l.lineID === activeLineId) || null;
  }, [activeLineId, lines]);

  // Find the next uncounted line (for the "next" suggestion)
  const nextUncountedLine = useMemo(() => {
    return lines.find(l => l.lineStatus < 50);
  }, [lines]);

  // Global barcode scanner for quick counting
  useSimpleBarcodeScanner({
    enabled: barcodeScannerEnabled && activeLineId === null,
    onScan: (barcode: string) => {
      handleBarcodeScan(barcode);
    }
  });

  const handleBarcodeScan = (value: string) => {
    if (!value.trim()) return;

    // Parse GS1 if applicable
    const looksLikeGS1 = value.includes(String.fromCharCode(29)) || value.includes('~') || value.includes('§') || value.startsWith(']C1');
    let scannedItemNo = value.replace(/[~§]+$/g, '').trim();

    if (looksLikeGS1) {
      const gs1Data = parseGS1Barcode(value);
      if (gs1Data.gtin) {
        scannedItemNo = gs1Data.gtin;
      }
    }

    // Find a matching uncounted line
    // Support GTIN-13/14 matching: AI(01) always returns 14 digits (leading 0 + GTIN-13)
    const scannedLower = scannedItemNo.toLowerCase();
    const matchingLine = lines.find(l => {
      if (l.lineStatus >= 50) return false;
      const lineItemNo = l.itemNo?.toLowerCase() || '';
      const lineBarCode = l.itemBarCode?.toLowerCase() || '';
      // Exact match
      if (lineItemNo === scannedLower || lineBarCode === scannedLower) return true;
      // GTIN-14 scanned vs GTIN-13 stored
      if (scannedLower.length === 14) {
        if (lineBarCode.length === 13 && '0' + lineBarCode === scannedLower) return true;
        if (lineItemNo.length === 13 && '0' + lineItemNo === scannedLower) return true;
      }
      // GTIN-13 scanned vs GTIN-14 stored
      if (scannedLower.length === 13) {
        if (lineBarCode.length === 14 && lineBarCode === '0' + scannedLower) return true;
        if (lineItemNo.length === 14 && lineItemNo === '0' + scannedLower) return true;
      }
      return false;
    });

    if (matchingLine) {
      startCountingLine(matchingLine.lineID);
      setMessage({ text: `Vare funnet: ${matchingLine.itemNo} - ${matchingLine.description1 || ''}`, type: 'success' });
    } else {
      setMessage({ text: `Ingen utellet linje funnet for: ${scannedItemNo}`, type: 'error' });
    }
  };

  // Start counting a line - enters confirmation flow
  const startCountingLine = (lineId: number) => {
    setActiveLineId(lineId);
    setCountInput('');
    setLocationScanInput('');
    setItemScanInput('');
    setScanError(null);
    setConfirmStep(getFirstConfirmStep());
  };

  const handleStartCounting = (lineId: number) => {
    if (activeLineId === lineId) {
      // Toggle off
      cancelCounting();
    } else {
      startCountingLine(lineId);
    }
  };

  const cancelCounting = () => {
    setActiveLineId(null);
    setCountInput('');
    setLocationScanInput('');
    setItemScanInput('');
    setScanError(null);
    setConfirmStep('count');
  };

  // Location scan confirmation
  const handleLocationConfirm = () => {
    if (!activeLine) return;
    const expected = (activeLine.locationName || '').toLowerCase().trim();
    const scanned = locationScanInput.toLowerCase().trim();

    if (!scanned) {
      setScanError('Skann eller skriv inn lokasjon');
      return;
    }

    if (scanned !== expected) {
      setScanError(`Feil lokasjon: "${locationScanInput}". Forventet: "${activeLine.locationName}"`);
      return;
    }

    // Location confirmed — advance to next step
    setScanError(null);
    setConfirmStep(getNextStep('location'));
  };

  const handleLocationOverride = () => {
    // Allow override — skip location check
    setScanError(null);
    setConfirmStep(getNextStep('location'));
  };

  // Item scan confirmation
  const handleItemConfirm = () => {
    if (!activeLine) return;
    const scanned = itemScanInput.toLowerCase().trim();

    if (!scanned) {
      setScanError('Skann eller skriv inn varenummer');
      return;
    }

    const lineItemNo = activeLine.itemNo.toLowerCase().trim();
    const lineBarCode = activeLine.itemBarCode?.toLowerCase().trim() || '';
    // Exact match
    let matchesItemNo = scanned === lineItemNo;
    let matchesBarcode = !!lineBarCode && scanned === lineBarCode;
    // GTIN-13/14 padding match
    if (!matchesItemNo && !matchesBarcode) {
      if (scanned.length === 14) {
        matchesBarcode = lineBarCode.length === 13 && '0' + lineBarCode === scanned;
        matchesItemNo = lineItemNo.length === 13 && '0' + lineItemNo === scanned;
      } else if (scanned.length === 13) {
        matchesBarcode = lineBarCode.length === 14 && lineBarCode === '0' + scanned;
        matchesItemNo = lineItemNo.length === 14 && lineItemNo === '0' + scanned;
      }
    }

    if (!matchesItemNo && !matchesBarcode) {
      setScanError(`Feil vare: "${itemScanInput}". Forventet: "${activeLine.itemNo}"${activeLine.itemBarCode ? ` (${activeLine.itemBarCode})` : ''}`);
      return;
    }

    // Item confirmed — advance to next step (location if required, otherwise count)
    setScanError(null);
    setConfirmStep(getNextStep('item'));
  };

  const handleSaveCount = async (lineId: number, forceOverride = false) => {
    const qty = parseFloat(countInput);
    if (isNaN(qty) || qty < 0) {
      setMessage({ text: 'Ugyldig antall', type: 'error' });
      return;
    }

    // Check against MaxLineItemQuantity (unless already overridden)
    if (!forceOverride && maxLineItemQuantity != null && qty > maxLineItemQuantity) {
      setPendingSaveLineId(lineId);
      setPendingSaveQty(qty);
      setShowQuantityWarning(true);
      return;
    }

    setSaving(true);
    try {
      await countingListCreationService.recordLineCount(list.batchID, lineId, qty);

      // Update local state
      setLines(prev => prev.map(l =>
        l.lineID === lineId
          ? { ...l, quantity: qty, lineStatus: 50 }
          : l
      ));

      cancelCounting();
      setMessage({ text: `Telling registrert: ${qty}`, type: 'success' });

      // Auto-advance to next uncounted line after a short delay
      setTimeout(() => {
        setMessage(null);
        const updatedLines = lines.map(l =>
          l.lineID === lineId ? { ...l, lineStatus: 50 } : l
        );
        const next = updatedLines.find(l => l.lineStatus < 50);
        if (next) {
          startCountingLine(next.lineID);
        }
      }, 1500);
    } catch (err) {
      console.error('Error saving count:', err);
      setMessage({ text: 'Feil ved lagring av telling', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Handlers for quantity warning modal
  const handleQuantityWarningConfirm = () => {
    setShowQuantityWarning(false);
    if (pendingSaveLineId != null) {
      handleSaveCount(pendingSaveLineId, true);
    }
    setPendingSaveLineId(null);
    setPendingSaveQty(0);
  };

  const handleQuantityWarningCancel = () => {
    setShowQuantityWarning(false);
    setPendingSaveLineId(null);
    setPendingSaveQty(0);
    setCountInput('');
    // Re-focus the count input so user can enter new value
    setTimeout(() => countInputRef.current?.focus(), 100);
  };

  // Reset a counted line so operator can recount
  const handleResetLine = async (lineId: number) => {
    const line = lines.find(l => l.lineID === lineId);
    if (!line) return;

    const confirmed = window.confirm(
      `Vil du nullstille tellingen for ${line.itemNo} på lokasjon ${line.locationName} og telle på nytt?\n\nTelt antall (${line.quantity ?? 0}) vil bli fjernet.`
    );
    if (!confirmed) return;

    try {
      setSaving(true);
      await countingListCreationService.resetLineCount(list.batchID, lineId);

      // Update local state
      setLines(prev => prev.map(l =>
        l.lineID === lineId
          ? { ...l, lineStatus: 20, quantity: 0 }
          : l
      ));

      setMessage({ text: `Linje nullstilt — tell på nytt`, type: 'success' });

      // Start counting flow for this line (requires re-scan of item/location)
      startCountingLine(lineId);
    } catch (err) {
      console.error('Error resetting line count:', err);
      setMessage({ text: 'Feil ved nullstilling av tellelinje', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteList = async () => {
    const counted = lines.filter(l => l.lineStatus >= 50).length;
    const uncounted = lines.length - counted;

    let confirmMsg = `Fullfør tellelisten? ${counted} av ${lines.length} linjer er telt.`;
    if (uncounted > 0) {
      confirmMsg += `\n\n⚠️ ${uncounted} linje(r) er ikke telt. Disse vil stå som 0.`;
    }
    confirmMsg += '\n\nTellelisten sendes til lagersjef for gjennomgang.';

    if (!window.confirm(confirmMsg)) return;

    setCompleting(true);
    try {
      const result = await countingListCreationService.completeCountingList(list.batchID);
      if (result.success) {
        setMessage({ text: 'Tellelisten er fullført og sendt til lagersjef', type: 'success' });
        setTimeout(() => {
          onCompleted?.();
          onBack();
        }, 1500);
      } else {
        setMessage({ text: result.errorMessage || 'Feil ved fullføring', type: 'error' });
      }
    } catch (err) {
      console.error('Error completing counting list:', err);
      setMessage({ text: 'Feil ved fullføring av telleliste', type: 'error' });
    } finally {
      setCompleting(false);
    }
  };

  const completedCount = lines.filter(l => l.lineStatus >= 50).length;
  const totalCount = lines.length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader className="animate-spin h-8 w-8 text-blue-500 mb-2" />
        <span className="text-sm text-gray-500">Laster tellelistelinjer...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-10 w-10 text-red-400 mb-3" />
        <p className="text-sm text-red-600 mb-3">{error}</p>
        <button
          onClick={loadLines}
          className="flex items-center space-x-1 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
        >
          <RefreshCw className="h-4 w-4" />
          <span>Prøv igjen</span>
        </button>
      </div>
    );
  }

  // Determine the line to display in the active box
  const activeBoxLine = activeLine || nextUncountedLine;
  const isActiveBoxCounting = activeLine !== null;

  // Step indicator labels (Order: Item → Location → Count)
  const stepLabel = confirmStep === 'item' ? 'Steg 1: Bekreft vare'
    : confirmStep === 'location' ? (requireItemScan ? 'Steg 2: Bekreft lokasjon' : 'Steg 1: Bekreft lokasjon')
    : 'Registrer antall';

  return (
    <div className="space-y-3">
      {/* Mobile scanner support */}
      <MobileBarcodeCaptureInput
        enabled={barcodeScannerEnabled && activeLineId === null}
        onScan={handleBarcodeScan}
      />

      {/* Header with back button */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-800"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Tilbake til lister</span>
        </button>
        <div className="flex items-center gap-2">
          {/* Show active confirm settings */}
          {profileLoaded && (requireLocationScan || requireItemScan) && (
            <div className="flex items-center gap-1 text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded-full">
              <ShieldCheck className="h-3 w-3" />
              <span>
                {requireItemScan && requireLocationScan ? 'Vare+Lok' : requireItemScan ? 'Vare' : 'Lok'}
              </span>
            </div>
          )}
          <button
            onClick={loadLines}
            className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
            title="Oppdater"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* List info */}
      <div className="bg-white border rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="min-w-0">
            <h3 className="font-medium text-sm text-gray-900 truncate">
              {list.description1 || list.batchID}
            </h3>
            <p className="text-xs text-gray-500 font-mono">{list.batchID}</p>
          </div>
          {progressPercent === 100 && (
            <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0" />
          )}
        </div>
        {/* Progress */}
        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
          <span>{completedCount} / {totalCount} linjer telt</span>
          <span>{progressPercent}%</span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              progressPercent === 100 ? 'bg-green-500' : 'bg-blue-500'
            }`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        {/* Complete button */}
        {totalCount > 0 && (
          <button
            onClick={handleCompleteList}
            disabled={completing}
            className={`mt-3 w-full flex items-center justify-center space-x-2 px-4 py-2.5 rounded-lg font-medium text-sm min-h-[44px] transition-colors ${
              completedCount === totalCount
                ? 'bg-green-500 text-white hover:bg-green-600'
                : 'bg-orange-500 text-white hover:bg-orange-600'
            }`}
          >
            {completing ? (
              <Loader className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            <span>
              {completedCount === totalCount
                ? 'Fullfør telleliste'
                : `Fullfør telleliste (${completedCount}/${totalCount} telt)`
              }
            </span>
          </button>
        )}
      </div>

      {/* ===== ACTIVE COUNTING BOX ===== */}
      {activeBoxLine && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg shadow-sm p-3 sm:p-4">
          {/* MOBILE layout */}
          <div className="sm:hidden">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2.5 h-2.5 bg-blue-500 rounded-full animate-pulse flex-shrink-0" />
              <span className="text-xs font-semibold text-blue-900 uppercase tracking-wide">
                {isActiveBoxCounting ? 'Aktiv telling' : 'Neste lokasjon'}
              </span>
              {isActiveBoxCounting && (requireLocationScan || requireItemScan) && (
                <span className="text-xs text-orange-600 ml-auto">{stepLabel}</span>
              )}
            </div>
            <div className="flex items-center gap-2 mb-2 bg-white rounded-md p-2.5 border border-blue-200">
              <MapPin className="h-5 w-5 text-blue-600 flex-shrink-0" />
              <span className="text-lg font-bold text-gray-900">{activeBoxLine.locationName}</span>
            </div>
            <div className="mb-2">
              <div className="font-bold text-sm text-gray-900 truncate">{activeBoxLine.itemNo}</div>
              <div className="text-xs text-gray-600 truncate">{activeBoxLine.description1}</div>
            </div>
            <div className="flex items-center gap-1 text-sm text-gray-700">
              <Package className="h-4 w-4 text-blue-600" />
              <span>Forventet: <strong>{activeBoxLine.quantityOrigin ?? 0}</strong> {activeBoxLine.itemUnitCode || 'stk'}</span>
            </div>
          </div>

          {/* DESKTOP layout - 3-column grid */}
          <div className="hidden sm:block">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2.5 h-2.5 bg-blue-500 rounded-full animate-pulse" />
              <h3 className="text-base font-semibold text-blue-900">
                {isActiveBoxCounting ? 'Aktiv telling' : 'Neste lokasjon'}
              </h3>
              {isActiveBoxCounting && (requireLocationScan || requireItemScan) && (
                <span className="ml-auto text-sm font-medium text-orange-600 bg-orange-50 px-2.5 py-0.5 rounded-full">
                  {stepLabel}
                </span>
              )}
            </div>
            <div className="bg-white rounded-md p-5 border border-blue-200">
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin size={18} className="text-blue-600" />
                    <span className="text-sm font-medium text-gray-600">Lokasjon</span>
                    {isActiveBoxCounting && confirmStep === 'location' && (
                      <Scan size={14} className="text-orange-500 animate-pulse" />
                    )}
                  </div>
                  <div className="text-2xl font-bold text-gray-900">{activeBoxLine.locationName}</div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Package size={18} className="text-blue-600" />
                    <span className="text-sm font-medium text-gray-600">Vare</span>
                    {isActiveBoxCounting && confirmStep === 'item' && (
                      <Scan size={14} className="text-orange-500 animate-pulse" />
                    )}
                  </div>
                  <div className="text-xl font-bold text-gray-900">{activeBoxLine.itemNo}</div>
                  <div className="text-sm text-gray-600 truncate">{activeBoxLine.description1}</div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle size={18} className="text-blue-600" />
                    <span className="text-sm font-medium text-gray-600">Forventet antall</span>
                  </div>
                  <div className="text-xl font-bold text-gray-900">
                    {activeBoxLine.quantityOrigin ?? 0} <span className="text-base font-normal text-gray-500">{activeBoxLine.itemUnitCode || 'stk'}</span>
                  </div>
                  {activeBoxLine.lineStatus >= 50 && (
                    <div className="text-sm font-semibold text-green-600 mt-1">
                      Telt: {activeBoxLine.quantity ?? 0}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ===== CONFIRMATION STEPS (inline in active box) ===== */}
          {isActiveBoxCounting && activeLine && activeLine.lineStatus < 50 && (
            <div className="mt-3">
              {/* Scan error */}
              {scanError && (
                <div className="flex items-center space-x-2 px-3 py-2 mb-2 rounded-lg text-sm bg-red-50 text-red-800 border border-red-200">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{scanError}</span>
                </div>
              )}

              {/* STEP: Location scan */}
              {confirmStep === 'location' && (
                <div className="bg-white rounded-lg p-3 border border-orange-200">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="h-4 w-4 text-orange-500" />
                    <span className="text-sm font-medium text-orange-700">Skann lokasjon for å bekrefte</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      ref={locationScanRef}
                      type="text"
                      value={locationScanInput}
                      onChange={(e) => { setLocationScanInput(e.target.value); setScanError(null); }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleLocationConfirm();
                        if (e.key === 'Escape') cancelCounting();
                      }}
                      placeholder={`Skann lokasjon (${activeLine.locationName})`}
                      className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base min-h-[44px] bg-white"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={handleLocationConfirm}
                      disabled={!locationScanInput.trim()}
                      className={`px-4 py-2.5 rounded-lg font-medium text-sm min-h-[44px] ${
                        !locationScanInput.trim()
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      Bekreft
                    </button>
                    <button
                      type="button"
                      onClick={handleLocationOverride}
                      className="px-3 py-2.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 font-medium text-sm min-h-[44px]"
                    >
                      Overstyr
                    </button>
                    <button
                      type="button"
                      onClick={cancelCounting}
                      className="p-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 min-h-[44px] min-w-[44px] flex items-center justify-center bg-white"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP: Item scan */}
              {confirmStep === 'item' && (
                <div className="bg-white rounded-lg p-3 border border-orange-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Package className="h-4 w-4 text-orange-500" />
                    <span className="text-sm font-medium text-orange-700">Skann vare for å bekrefte</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      ref={itemScanRef}
                      type="text"
                      value={itemScanInput}
                      onChange={(e) => { setItemScanInput(e.target.value); setScanError(null); }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleItemConfirm();
                        if (e.key === 'Escape') cancelCounting();
                      }}
                      placeholder={`Skann vare (${activeLine.itemNo})`}
                      className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base min-h-[44px] bg-white"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={handleItemConfirm}
                      disabled={!itemScanInput.trim()}
                      className={`px-4 py-2.5 rounded-lg font-medium text-sm min-h-[44px] ${
                        !itemScanInput.trim()
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      Bekreft
                    </button>
                    <button
                      type="button"
                      onClick={cancelCounting}
                      className="p-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 min-h-[44px] min-w-[44px] flex items-center justify-center bg-white"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP: Count input */}
              {confirmStep === 'count' && (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSaveCount(activeLine.lineID);
                  }}
                  className="flex items-center space-x-2"
                >
                  {/* Show confirmed badges if steps were completed */}
                  {(requireLocationScan || requireItemScan) && (
                    <div className="flex items-center gap-1 mr-1">
                      {requireLocationScan && (
                        <span className="inline-flex items-center gap-0.5 text-xs text-green-700 bg-green-50 px-1.5 py-0.5 rounded">
                          <CheckCircle2 className="h-3 w-3" />
                          Lok
                        </span>
                      )}
                      {requireItemScan && (
                        <span className="inline-flex items-center gap-0.5 text-xs text-green-700 bg-green-50 px-1.5 py-0.5 rounded">
                          <CheckCircle2 className="h-3 w-3" />
                          Vare
                        </span>
                      )}
                    </div>
                  )}
                  <input
                    ref={countInputRef}
                    type="number"
                    value={countInput}
                    onChange={(e) => setCountInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') cancelCounting();
                    }}
                    placeholder="Telt antall"
                    className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base font-semibold min-h-[44px] bg-white"
                    min="0"
                    step="0.01"
                    enterKeyHint="done"
                    autoFocus
                  />
                  <button
                    type="submit"
                    disabled={saving || !countInput.trim()}
                    className={`px-5 py-2.5 rounded-lg font-medium text-sm min-h-[44px] flex items-center space-x-1 ${
                      saving || !countInput.trim()
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-green-500 text-white hover:bg-green-600'
                    }`}
                  >
                    {saving ? (
                      <Loader className="h-4 w-4 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4" />
                    )}
                    <span>Lagre</span>
                  </button>
                  <button
                    type="button"
                    onClick={cancelCounting}
                    className="p-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 min-h-[44px] min-w-[44px] flex items-center justify-center bg-white"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </form>
              )}
            </div>
          )}

          {/* Click hint when showing next uncounted (not actively counting) */}
          {!isActiveBoxCounting && nextUncountedLine && (
            <button
              onClick={() => handleStartCounting(nextUncountedLine.lineID)}
              className="mt-3 w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-blue-500 text-white rounded-lg font-medium text-sm min-h-[44px] hover:bg-blue-600 transition-colors"
            >
              <Scan className="h-4 w-4" />
              <span>Start telling på denne lokasjonen</span>
            </button>
          )}
        </div>
      )}

      {/* Scanner hint */}
      {activeLineId === null && (
        <div className="flex items-center space-x-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
          <Scan className="h-4 w-4 text-blue-500 flex-shrink-0" />
          <span className="text-xs text-blue-700">Skann en strekkode for å hoppe til riktig linje, eller velg en linje manuelt</span>
        </div>
      )}

      {/* Message */}
      {message && (
        <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm ${
          message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
          ) : (
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {/* Lines list */}
      <div className="bg-white border rounded-lg divide-y divide-gray-100 overflow-hidden">
        {lines.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <Package className="h-10 w-10 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">Ingen linjer i denne tellelisten</p>
          </div>
        ) : (
          lines.map((line) => {
            const isCounted = line.lineStatus >= 50;
            const isActive = activeLineId === line.lineID;

            return (
              <div
                key={line.lineID}
                className={`${isActive ? 'bg-blue-50 ring-2 ring-blue-300 ring-inset' : isCounted ? 'bg-green-50/30' : 'hover:bg-gray-50'}`}
              >
                <button
                  onClick={() => {
                    if (isCounted && !isActive) {
                      // Allow reset only if list is not completed
                      if (list.orderStatus < 50) {
                        handleResetLine(line.lineID);
                      }
                    } else if (!isCounted) {
                      handleStartCounting(line.lineID);
                    }
                  }}
                  className="w-full text-left p-3 flex items-center"
                  disabled={isCounted && list.orderStatus >= 50}
                >
                  <div className="flex-shrink-0 mr-3">
                    {isCounted ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    ) : isActive ? (
                      <ChevronDown className="h-5 w-5 text-blue-500" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-1">
                      <MapPin className="h-4 w-4 text-blue-600 flex-shrink-0" />
                      <span className="font-bold text-base text-gray-900">{line.locationName}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="font-mono text-sm font-medium truncate">{line.itemNo}</span>
                      <span className="text-xs text-gray-500 truncate">{line.description1}</span>
                    </div>
                    <div className="flex items-center space-x-3 mt-0.5 text-xs text-gray-500">
                      <span>Forventet: {line.quantityOrigin ?? 0} {line.itemUnitCode || 'stk'}</span>
                      {isCounted && (
                        <span className="text-green-700 font-medium">
                          Telt: {line.quantity ?? 0}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* ===== QUANTITY WARNING MODAL ===== */}
      {showQuantityWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full overflow-hidden">
            {/* Header */}
            <div className="bg-orange-50 border-b border-orange-200 px-5 py-4 flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 text-base">Uvanlig høyt antall</h3>
                <p className="text-xs text-orange-700 mt-0.5">Maks anbefalt: {maxLineItemQuantity}</p>
              </div>
            </div>
            {/* Body */}
            <div className="px-5 py-4">
              <p className="text-sm text-gray-700">
                Du har tastet inn <span className="font-bold text-gray-900">{pendingSaveQty}</span>, som overstiger maks antall per linje (<span className="font-bold">{maxLineItemQuantity}</span>).
              </p>
              <p className="text-sm text-gray-600 mt-2">
                Er du sikker på at du vil registrere dette antallet?
              </p>
            </div>
            {/* Footer */}
            <div className="px-5 py-3 bg-gray-50 border-t flex gap-3">
              <button
                onClick={handleQuantityWarningCancel}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors min-h-[44px]"
              >
                Nei, endre antall
              </button>
              <button
                onClick={handleQuantityWarningConfirm}
                className="flex-1 px-4 py-2.5 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors min-h-[44px]"
              >
                Ja, registrer {pendingSaveQty}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
